import { InputHTMLAttributes, ReactNode, useEffect, useRef } from "react";
import { cva, VariantProps } from "class-variance-authority";
import clsx from "clsx";
import { Check, Minus } from "lucide-react";

const box = cva(
	[
		"flex shrink-0 items-center justify-center rounded transition-colors border ring-offset-2 cursor-pointer",
		"peer-disabled:bg-navy-300 peer-disabled:cursor-default peer-disabled:border-navy-500 peer-disabled:hover:border-navy-500 peer-disabled:hover:bg-navy-300 peer-disabled:peer-checked:text-navy-800",
		"peer-checked:[&>svg]:opacity-100 peer-indeterminate:[&>svg]:opacity-100",
		"peer-focus-visible:ring-1",
	],
	{
		variants: {
			variant: {
				blue: [
					"bg-white border-sky-500 text-white",
					"hover:border-sky-600 hover:bg-sky-200",
					"peer-focus-visible:ring-sky-500",
					"peer-checked:bg-sky-500 peer-checked:border-sky-500",
					"peer-indeterminate:bg-sky-500 peer-indeterminate:border-sky-500",
					"peer-checked:hover:bg-sky-600 peer-checked:hover:border-sky-600",
					"peer-indeterminate:hover:bg-sky-600 peer-indeterminate:hover:border-sky-600",
				],
				dark: [
					"bg-transparent border-Border-primary text-white",
					"hover:border-navy-900",
					"peer-focus-visible:ring-navy-900",
					"peer-checked:bg-navy-900",
					"peer-indeterminate:bg-slate-900",
				],
				greyBlue: [
					"bg-white border-gray-400 text-white",
					"hover:border-sky-600 hover:bg-sky-200",
					"peer-focus-visible:ring-sky-500",
					"peer-checked:bg-sky-500 peer-checked:border-sky-500",
					"peer-indeterminate:bg-sky-500 peer-indeterminate:border-sky-500",
					"peer-checked:hover:bg-sky-600 peer-checked:hover:border-sky-600",
					"peer-indeterminate:hover:bg-sky-600 peer-indeterminate:hover:border-sky-600",
				],
			},
			size: {
				md: "w-6 h-6",
				sm: "w-4.5 h-4.5",
			},
			error: {
				true: "",
				false: "",
			},
		},
		compoundVariants: [
			{
				error: true,
				class: [
					"!bg-red-200 !border-red-500 !text-red-500",
					"!peer-focus-visible:ring-rose-600",
					"!peer-checked:bg-red-600 !peer-checked:border-red-600",
					"!peer-indeterminate:bg-red-600 !peer-indeterminate:border-red-600",
					"!peer-checked:[&>svg]:text-red-500 !peer-indeterminate:[&>svg]:text-red-500",
				],
			},
		],
		defaultVariants: {
			variant: "blue",
			size: "md",
			error: false,
		},
	}
);

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size">, VariantProps<typeof box> {
	indeterminate?: boolean;
	label?: ReactNode;
}

export function Checkbox({ variant, size, error, indeterminate = false, disabled, label, className, ...props }: CheckboxProps) {
	const ref = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (ref.current) ref.current.indeterminate = indeterminate;
	}, [indeterminate]);

	const Icon = indeterminate ? Minus : Check;
	const iconSize = size === "sm" ? 16 : 20;

	return (
		<label className="inline-flex items-center gap-2 select-none">
			<input ref={ref} type="checkbox" disabled={disabled} className="sr-only peer" {...props} />
			<span className={clsx(box({ variant, size, error }), className)}>
				<Icon size={iconSize} strokeWidth={2.25} aria-hidden="true" className="opacity-0 pointer-events-none transition-opacity" />
			</span>
			{label && <span className="font-light text-Text-primary leading-tight">{label}</span>}
		</label>
	);
}
